import React from "react";
import { ArrowUpRight, ShieldCheck, Sparkles } from "lucide-react";

const HERO_ILLUSTRATION =
    "https://customer-assets.emergentagent.com/job_afad2a6d-7dba-488f-b2fd-a41c3f20dfe4/artifacts/h5o7iqq9_Brandstyle%20%20Illustraties%202.jpg";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden bg-white pt-16 lg:pt-24 pb-16 lg:pb-28"
        >
            {/* Background ornaments */}
            <div
                aria-hidden
                className="absolute inset-0 grid-bg opacity-60 pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(45,212,212,0.18), transparent 70%)",
                }}
            />
            <div
                aria-hidden
                className="absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(31,41,55,0.06), transparent 70%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bpo-cyanLight border border-bpo-cyan/30 text-bpo-charcoal eyebrow"
                        >
                            <Sparkles className="h-3.5 w-3.5 text-bpo-cyanDark" />
                            EU-registered · Remote-first · Sofia, Bulgaria
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="font-display mt-6 text-4xl sm:text-5xl lg:text-[64px] xl:text-[72px] font-extrabold leading-[1.02] tracking-tight text-bpo-ink"
                        >
                            Proven consulting for
                            <span className="block">
                                <span className="text-bpo-cyan">purpose-driven</span>{" "}
                                growth.
                            </span>
                        </h1>

                        <p
                            data-testid="hero-subheadline"
                            className="mt-7 text-lg md:text-xl text-bpo-charcoal/80 max-w-2xl leading-relaxed"
                        >
                            Business Performance Outlook helps startups, scaleups
                            and small-to-mid-sized enterprises unlock sustainable
                            value with accessible, affordable systems built on{" "}
                            <span className="font-semibold text-bpo-ink">
                                ISO 20700
                            </span>{" "}
                            and certified Six Sigma Black Belt, Prosci, PMP,
                            Vested and COPC standards — delivered remotely across
                            the EU.
                        </p>

                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a
                                href="https://app.kadonetworks.com/kado/bposolutions"
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="hero-primary-cta"
                                className="inline-flex items-center justify-center gap-2 bg-bpo-cyan hover:bg-bpo-cyanDark text-white font-semibold py-3.5 px-7 rounded-full transition-all shadow-md shadow-bpo-cyan/30 hover:shadow-lg hover:-translate-y-0.5"
                            >
                                Book a consultation
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                            <a
                                href="#contact"
                                data-testid="hero-secondary-cta"
                                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-bpo-ink border-2 border-bpo-ink font-semibold py-3.5 px-7 rounded-full transition-all"
                            >
                                Send a brief
                            </a>
                        </div>

                        <div
                            data-testid="hero-trust-row"
                            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-[0.18em] font-semibold text-gray-500"
                        >
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4 text-bpo-cyanDark" />
                                ISO 20700 aligned
                            </span>
                            <span>·</span>
                            <span>EU TAX ID BG 206101677</span>
                            <span>·</span>
                            <span>D-U-N-S® 525487959</span>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative rounded-3xl bg-gradient-to-br from-bpo-cyanLight via-white to-white border border-gray-200 shadow-[0_30px_60px_-30px_rgba(45,212,212,0.4)] p-6 md:p-8">
                            <img
                                src={HERO_ILLUSTRATION}
                                alt="BPO Solutions brand illustration — customer experience, value enablement and sustainable lifecycle"
                                className="w-full h-auto rounded-2xl object-cover"
                                loading="eager"
                            />
                            <div className="absolute -top-4 -left-4 hidden md:block">
                                <div className="px-4 py-2 rounded-full bg-white border border-gray-200 shadow-md text-[11px] uppercase tracking-[0.18em] font-bold text-bpo-charcoal">
                                    Value enablement
                                </div>
                            </div>
                            <div className="absolute -bottom-5 -right-5 hidden md:block">
                                <div className="px-4 py-2 rounded-full bg-bpo-ink text-white shadow-lg text-[11px] uppercase tracking-[0.18em] font-bold">
                                    Sustainable lifecycle
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 mt-6">
                            {[
                                { k: "12+", v: "Frameworks deployed" },
                                { k: "EU", v: "Remote delivery" },
                                { k: "100%", v: "Aligned outcomes" },
                            ].map((s, i) => (
                                <div
                                    key={i}
                                    data-testid={`hero-stat-${i}`}
                                    className="rounded-2xl bg-white border border-gray-200 p-4 text-center"
                                >
                                    <div className="font-display text-2xl font-extrabold text-bpo-ink">
                                        {s.k}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-500 mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
